/**
 * Reads the server-rendered browser message catalog and performs placeholder
 * substitution. Missing or malformed catalogs fail visibly instead of silently
 * falling back to partial hard-coded interface text.
 */
import { throwFrontendError } from './errors'
import { Log } from './logger'

export type TranslationParameters = Record<string, string | number>

type Messages = Record<string, string>

/**
 * Parses the localized browser catalog and reports malformed page data explicitly.
 */
function readMessages(): Messages {
  const element = document.getElementById('browser-messages')

  if (element === null) {
    throwFrontendError(new Error('Browser message catalog element is missing.'))
  }

  try {
    const parsed: unknown = JSON.parse(element.textContent ?? '{}')

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new TypeError('Browser message catalog must be a JSON object.')
    }

    const entries = Object.entries(parsed)

    if (entries.some((entry) => typeof entry[1] !== 'string')) {
      throw new TypeError('Every browser message must be a string.')
    }

    return Object.fromEntries(entries) as Messages
  } catch (error) {
    throwFrontendError(error)
  }
}

const messages = Log.info('Load browser translations', () => readMessages())

export const locale = document.documentElement.lang || 'en'

const pluralRules = new Intl.PluralRules(locale)

/**
 * Picks the plural category entry (key.one, key.other…) for a numeric count, falling back to key.other.
 */
function pluralMessage(key: string, count: string | number | undefined): string | undefined {
  if (typeof count !== 'number') {
    return undefined
  }

  return messages[`${key}.${pluralRules.select(count)}`] ?? messages[`${key}.other`]
}

/**
 * Resolves a localized browser message, choosing its plural form by count, and substitutes its named parameters.
 */
export function translate(key: string, parameters: TranslationParameters = {}): string {
  let message = messages[key] ?? pluralMessage(key, parameters.count) ?? key

  for (const [name, value] of Object.entries(parameters)) {
    message = message.replaceAll(`{${name}}`, String(value))
  }

  return message
}
